/**
 * Renders one Google carousel card and, inline beneath it, the images it owns.
 * The parent list fetches every card's images in one batch and hands each card
 * its share, so this block issues no query of its own. Laid out like the ADSP
 * creative block so nested Ads Manager levels read the same across publishers.
 */
import React from 'react';
import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import { useTranslation } from 'src/arb-wrapper';
import type { PublisherEntityDto } from 'src/store/generated/leela-api-generated';
import { narrowCarouselCardMetadata } from 'src/utils/publisherEntityMetadata';
import { compareByStatus } from 'src/utils/publisherEntityStatus';
import { EntityAssetCard } from './EntityAssetCard';
import { EntityDetailsToggle } from './EntityDetailsToggle';
import { EntityIcon } from './EntityIcon';
import { EMPTY_VALUE, StatusBadge } from './StatusBadge';

interface CarouselCardBlockProps {
  card: PublisherEntityDto;
  images: readonly PublisherEntityDto[];
}

export const CarouselCardBlock: React.FC<CarouselCardBlockProps> = ({ card, images }) => {
  const { t } = useTranslation();
  const metadata = narrowCarouselCardMetadata(card.publisherMetadata);
  const cardIndex = metadata?.cardIndex;
  const sortedImages = [...images].sort(compareByStatus);

  return (
    <Box data-testid={`ads-manager-carousel-card-${card._id}`} spacingInset="300" type="outline">
      <Column spacing="200">
        <Row alignmentHorizontal="justify" alignmentVertical="center">
          <Row alignmentVertical="center" spacing="200">
            <EntityIcon size={24} type={card.publisherEntityType} />
            {cardIndex !== undefined && (
              <Text data-testid={`ads-manager-carousel-card-position-${card._id}`} type="b200">
                {t('ads_manager_carousel_card_position', { position: cardIndex + 1 })}
              </Text>
            )}
            <Text color="secondary" type="b200">{metadata?.headline ?? EMPTY_VALUE}</Text>
          </Row>
          <StatusBadge entity={card} />
        </Row>
        {card.lastError != null && (
          <Text color="error" type="b100">{card.lastError}</Text>
        )}
        <EntityDetailsToggle entity={card} testId={`ads-manager-carousel-card-${card._id}`} />

        {sortedImages.length === 0
          ? (
            <Text
              color="secondary"
              data-testid={`ads-manager-carousel-card-no-images-${card._id}`}
              type="b100"
            >
              {t('ads_manager_carousel_card_no_images')}
            </Text>
            )
          : (
            <Column data-testid={`ads-manager-carousel-card-images-${card._id}`} spacing="200">
              <Text color="secondary" type="b100">
                {t('ads_manager_ad_details_assets')} ({sortedImages.length})
              </Text>
              <div style={{ maxWidth: 'calc(100vw - 440px)', overflowX: 'auto', overflowY: 'hidden', paddingBottom: 8, WebkitOverflowScrolling: 'touch' }}>
                <div style={{ display: 'inline-flex', gap: 12 }}>
                  {sortedImages.map(image => (
                    <EntityAssetCard entity={image} key={image._id} />
                  ))}
                </div>
              </div>
            </Column>
            )}
      </Column>
    </Box>
  );
};
